import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import type { Robot } from '../api/types';
import { useT } from '../i18n';
import { useLive, useRobotList } from '../live/store';
import { enableHandPush, lockWheels, useHandPushPending, useReleasedSerials } from '../lib/handPush';
import { usePopoverClose, useStoredState } from '../lib/hooks';
import { useRobotMode, type RobotMode } from '../lib/mode';

/** SVG icons from the canvas (no emoji). */
export const IconHand = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M18 11V6a2 2 0 0 0-4 0" />
    <path d="M14 10V4a2 2 0 0 0-4 0v2" />
    <path d="M10 10.5V6a2 2 0 0 0-4 0v8" />
    <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
  </svg>
);
export const IconLock = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const IconSpin = ({ size = 22 }: { size?: number }) => (
  <svg className="fab-spin" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
    <path d="M12 3a9 9 0 1 1-6.4 2.6" />
  </svg>
);

const blockedOf = (r: Robot | undefined) => (!r || !r.online ? 'offline' : r.status?.charging || r.status?.docked ? 'charging' : null);

/** The serial in /robots/:serial and /robots/:serial/sensors. */
function useRouteRobot(): { serial: string | null; drivePage: boolean } {
  const loc = useLocation();
  const m = /^\/robots\/([^/]+)(\/sensors)?\/?$/.exec(loc.pathname);
  return m ? { serial: decodeURIComponent(m[1]!), drivePage: !m[2] } : { serial: null, drivePage: false };
}

/**
 * Hand push FAB (canvas: "Site: Hand push FAB", "… · popover", "Phone", "Hand push states"): one click toggles the
 * target robot's wheel brakes (the route's robot, else the only online robot, else the one robot already in hand
 * push); with several robots it opens a popover with a switch per robot and "Lock all wheels". The Robot page docks
 * its own copy in the video card (`docked`), so the floating one is hidden there.
 */
export function HandPushFab({ docked, serial: fixedSerial }: { docked?: boolean; serial?: string }) {
  const t = useT();
  const route = useRouteRobot();
  const robots = useRobotList();
  const pending = useHandPushPending((s) => s.pending);
  const released = useReleasedSerials();
  const [open, setOpen] = useState(false);
  const [last, setLast] = useStoredState<string>('cruzr.handPushLast', '');
  const ref = usePopoverClose(open, () => setOpen(false));
  const footerBottom = useFooterDock(!docked);
  if (!docked && route.drivePage) return null; // the Robot page shows the docked one
  const online = robots.filter((r) => r.online);
  const targetSerial = fixedSerial ?? route.serial ?? (online.length === 1 ? online[0]!.serial : null);
  const target = targetSerial ? robots.find((r) => r.serial === targetSerial) : undefined;
  if (!docked && !target && !online.length) return null;
  const nameOf = (s: string) => robots.find((r) => r.serial === s)?.name ?? s;
  const n = released.length;

  // what the button shows and does
  let tone: 'off' | 'on' | 'pending' | 'disabled' = 'off';
  let label: string;
  let icon = <IconHand />;
  let onClick: (() => void) | null = null;
  const pendingSerial = target ? (pending[target.serial] ? target.serial : null) : (Object.keys(pending)[0] ?? null);
  if (pendingSerial) {
    tone = 'pending';
    icon = <IconSpin />;
    // the Robot page's docked copy sits next to the robot's name: no need to repeat it
    label = docked
      ? t(pending[pendingSerial] === 'release' ? 'fab.releasingShort' : 'fab.lockingShort')
      : t(pending[pendingSerial] === 'release' ? 'fab.releasing' : 'fab.locking', { name: nameOf(pendingSerial) });
  } else if (target) {
    const on = Boolean(target.status?.handPush?.enabled);
    const blocked = on ? null : blockedOf(target);
    if (blocked) {
      tone = 'disabled';
      label = docked ? t(blocked === 'offline' ? 'fab.offlineShort' : 'fab.chargingShort') : t(blocked === 'offline' ? 'fab.offline' : 'fab.charging');
    } else if (on) {
      tone = 'on';
      icon = <IconLock />;
      label = t('fab.lockName', { name: target.name });
      onClick = () => void lockWheels(target.serial);
    } else {
      label = t('fab.handPushName', { name: target.name });
      onClick = () => void enableHandPush(target.serial);
    }
  } else if (n === 1) {
    tone = 'on';
    icon = <IconLock />;
    label = t('fab.lockName', { name: nameOf(released[0]!) });
    onClick = () => void lockWheels(released[0]!);
  } else {
    tone = n > 1 ? 'on' : 'off';
    icon = n > 1 ? <IconLock /> : <IconHand />;
    label = n > 1 ? t('fab.lockN', { n }) : t('hand.button');
    onClick = () => setOpen(!open);
  }
  // the popover stays open while a switch in it is in flight (its row shows "Releasing…")
  const popover = !target && (n !== 1 || open);
  const summary = t(n === 1 ? 'fab.summaryOne' : 'fab.summaryN', { n });

  // popover rows: last chosen robot first, then the fleet order
  const rows = [...online].sort((a, b) => Number(b.serial === last) - Number(a.serial === last));
  return (
    <>
      {open && popover && (
        <div ref={ref} className="fab-pop" role="dialog" aria-label={t('hand.button')}>
          <div className="fab-pop-head">
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <h2>{t('hand.button')}</h2>
              <button type="button" className="icon-x" aria-label={t('common.close')} onClick={() => setOpen(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                  <path d="M6 6l12 12" />
                  <path d="M18 6 6 18" />
                </svg>
              </button>
            </div>
            <span>{t('fab.popHint')}</span>
          </div>
          <div className="fab-pop-rows">
            {rows.map((r) => (
              <FabRow key={r.serial} robot={r} onPick={() => setLast(r.serial)} />
            ))}
            {!rows.length && <div className="muted" style={{ padding: '10px 16px', fontSize: 13 }}>{t('fab.noneOnline')}</div>}
          </div>
          {n > 0 && (
            <div className="fab-pop-foot">
              <button type="button" className="btn sm" onClick={() => released.forEach((s) => void lockWheels(s))}>
                <IconLock size={14} />
                {t('fab.lockAll')}
              </button>
            </div>
          )}
        </div>
      )}
      <button
        type="button"
        className={`hand-fab tone-${tone}${docked ? ' docked' : ''}${footerBottom !== null ? ' in-footer' : ''}`}
        style={footerBottom !== null ? { bottom: footerBottom } : undefined}
        aria-pressed={tone === 'on'}
        aria-haspopup={popover ? 'dialog' : undefined}
        aria-expanded={popover ? open : undefined}
        aria-label={!docked && n > 0 ? `${label} · ${summary}` : label}
        title={label}
        disabled={tone === 'pending' || tone === 'disabled' || !onClick}
        onClick={() => onClick?.()}
      >
        {icon}
        <span className="fab-label">{label}</span>
        {!docked && n > 0 && (
          <span className="fab-badge" aria-hidden>
            {n}
          </span>
        )}
      </button>
    </>
  );
}

/**
 * When a panel footer with action buttons sits under the FAB's bottom-right corner (Maps "Save points", drawer
 * footers), the FAB becomes a compact circle centred in that footer's right end; the footer reserves the space
 * (CSS `body:has(.hand-fab.in-footer) .side-footer`). Returns the FAB's `bottom` in px, or null.
 */
function useFooterDock(active: boolean): number | null {
  const [bottom, setBottom] = useState<number | null>(null);
  useEffect(() => {
    if (!active) return;
    let raf = 0;
    const check = () => {
      raf = 0;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const hit = [...document.querySelectorAll<HTMLElement>('.side-footer')]
        .map((el) => el.getBoundingClientRect())
        .find((r) => r.width > 0 && r.height >= 48 && r.right >= vw - 40 && r.bottom >= vh - 40 && r.top < vh);
      setBottom(hit ? Math.max(4, Math.round(vh - hit.bottom + (hit.height - 56) / 2)) : null);
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(check);
    };
    const mo = new MutationObserver(schedule);
    mo.observe(document.querySelector('.main') ?? document.body, { childList: true, subtree: true });
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    schedule();
    return () => {
      mo.disconnect();
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [active]);
  return active ? bottom : null;
}

function modeChip(mode: RobotMode, pend: 'release' | 'lock' | undefined, t: ReturnType<typeof useT>): [string, string] {
  if (pend) return [pend === 'release' ? t('fab.chipReleasing') : t('fab.chipLocking'), 'busy'];
  switch (mode.kind) {
    case 'handPush':
      return [t('mode.handPush'), 'push'];
    case 'charging':
    case 'goingCharge':
      return [t('mode.charging'), 'charging'];
    case 'autonomy':
      return [t('mode.autonomy'), 'autonomy'];
    case 'tour':
      return [t('mode.tour'), 'tour'];
    case 'navigation':
      return [t('mode.navigation'), 'tour'];
    case 'estop':
      return [t('fab.chipEstop'), 'estop'];
    case 'stuck':
      return [t('mode.stuck'), 'tour'];
    default:
      return [t('fab.chipIdle'), 'idle'];
  }
}

function FabRow({ robot: r, onPick }: { robot: Robot; onPick: () => void }) {
  const t = useT();
  const mode = useRobotMode(r.serial);
  const pend = useHandPushPending((s) => s.pending[r.serial]);
  const on = Boolean(r.status?.handPush?.enabled);
  const blocked = on ? null : blockedOf(r);
  const [chip, tone] = modeChip(mode, pend, t);
  const swOn = pend ? pend === 'release' : on;
  return (
    <div className="fab-row">
      <span className={`dot ${blocked === 'charging' ? 'blue' : on ? 'blue' : 'green'}`} />
      <span className={`fab-row-name ellipsis${blocked ? ' muted' : ''}`}>{r.name}</span>
      <span className={`fab-chip ${tone}`}>{chip}</span>
      <button
        type="button"
        role="switch"
        aria-checked={swOn}
        aria-label={t('fab.switchFor', { name: r.name })}
        title={blocked === 'charging' ? t('fab.charging') : undefined}
        className={`toggle fab-switch${swOn ? ' on' : ''}`}
        disabled={Boolean(blocked) || Boolean(pend)}
        onClick={() => {
          onPick();
          void (on ? lockWheels(r.serial) : enableHandPush(r.serial));
        }}
      >
        <span />
      </button>
    </div>
  );
}

/** Fleet header chip: "1 robot in hand push". */
export function HandPushSummary() {
  const t = useT();
  const released = useReleasedSerials();
  const names = useLive((s) => released.map((k) => s.robots[k]?.name ?? k).join(', '));
  if (!released.length) return null;
  return (
    <span className="hand-summary" title={names}>
      <IconHand size={14} />
      {t(released.length === 1 ? 'fab.summaryOne' : 'fab.summaryN', { n: released.length })}
    </span>
  );
}
